import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for the Slack page (ADR-022 point 6, S26.07). `ops-copy.ts` puts
// them together with the rest into the one catalogue the screens read.

export type SlackCopyCatalog = typeof EN;

const EN = {
  // Slack (ADR-022 point 6, S26.07).
  titleSlack: "Slack · Back office · {product}",
  slackPageTitle: "Slack",
  slackPageDescription:
    "Channels staff hear from: a name, an incoming-webhook URL and the facts it hears. Staff only.",
  slackEmptyTitle: "No Slack channel is set up yet",
  slackEmptyDescription: "Add one to start hearing sign-ups, payments and delivery failures.",
  columnChannel: "Channel",
  columnTopics: "Hears",
  columnSetBy: "Set by",
  addSlackChannelAction: "Add a channel",
  testPostAction: "Post a test",
  removeAction: "Remove",
  addSlackChannelTitle: "Add a Slack channel",
  addSlackChannelDescription: "Paste the channel's incoming-webhook URL and choose what it hears.",
  addSlackChannelSubmit: "Add",
  editSlackChannelTitle: "Edit Slack channel",
  editSlackChannelDescription: "A field left blank keeps what is stored now.",
  editSlackChannelSubmit: "Save",
  removeSlackChannelTitle: "Remove this channel",
  removeSlackChannelDescription: "No further post is ever queued for it once removed.",
  removeSlackChannelSubmit: "Remove",
  testPostSlackChannelTitle: "Post a test message",
  testPostSlackChannelDescription: "Say why you are testing it; this reaches the real channel.",
  testPostSlackChannelSubmit: "Post test",
  fieldChannelName: "Name",
  fieldChannelNameHint: "How staff recognize it, typically the channel's own name.",
  fieldSlackWebhookUrl: "Webhook URL",
  fieldWebhookUrlHint: "Slack's incoming-webhook URL for this channel. Never shown again once saved.",
  fieldWebhookUrlEditHint: "Leave blank to keep the webhook already stored.",
  fieldTopics: "Facts it hears",
  topicSignUps: "Sign-ups",
  topicPayments: "Payments",
  topicDeliveryFailures: "Delivery failures",
  topicsProblem: "Choose at least one fact.",
  toastSlackChannelAdded: "Channel added",
  toastSlackChannelUpdated: "Channel updated",
  toastSlackChannelRemoved: "Channel removed",
  toastTestPostSent: "Test message posted",
  errorSlackChannelTitle: "The channel did not change",
  errorTestPostTitle: "No test message was posted",
} as const;

const ES: Translation<SlackCopyCatalog> = {
  titleSlack: "Slack · Back office · {product}",
  slackPageTitle: "Slack",
  slackPageDescription:
    "Canales por los que el staff se entera: un nombre, una URL de webhook y los hechos que escucha. Solo para staff.",
  slackEmptyTitle: "Todavía no hay ningún canal de Slack configurado",
  slackEmptyDescription: "Agrega uno para enterarte de registros, pagos y envíos fallidos.",
  columnChannel: "Canal",
  columnTopics: "Escucha",
  columnSetBy: "Definido por",
  addSlackChannelAction: "Agregar un canal",
  testPostAction: "Publicar una prueba",
  removeAction: "Quitar",
  addSlackChannelTitle: "Agregar un canal de Slack",
  addSlackChannelDescription: "Pega la URL de webhook del canal y elige qué va a escuchar.",
  addSlackChannelSubmit: "Agregar",
  editSlackChannelTitle: "Editar canal de Slack",
  editSlackChannelDescription: "Un campo que dejes vacío conserva lo que ya está guardado.",
  editSlackChannelSubmit: "Guardar",
  removeSlackChannelTitle: "Quitar este canal",
  removeSlackChannelDescription: "No se vuelve a encolar ninguna publicación para él una vez quitado.",
  removeSlackChannelSubmit: "Quitar",
  testPostSlackChannelTitle: "Publicar un mensaje de prueba",
  testPostSlackChannelDescription: "Indica por qué lo estás probando; esto llega al canal real.",
  testPostSlackChannelSubmit: "Publicar prueba",
  fieldChannelName: "Nombre",
  fieldChannelNameHint: "Cómo lo reconoce el staff, típicamente el nombre del propio canal.",
  fieldSlackWebhookUrl: "URL de webhook",
  fieldWebhookUrlHint: "La URL de webhook entrante de Slack para este canal. No se vuelve a mostrar.",
  fieldWebhookUrlEditHint: "Déjala vacía para conservar el webhook ya guardado.",
  fieldTopics: "Hechos que escucha",
  topicSignUps: "Registros",
  topicPayments: "Pagos",
  topicDeliveryFailures: "Envíos fallidos",
  topicsProblem: "Elige al menos un hecho.",
  toastSlackChannelAdded: "Canal agregado",
  toastSlackChannelUpdated: "Canal actualizado",
  toastSlackChannelRemoved: "Canal quitado",
  toastTestPostSent: "Mensaje de prueba publicado",
  errorSlackChannelTitle: "El canal no cambió",
  errorTestPostTitle: "No se publicó el mensaje de prueba",
};

export const SLACK_CATALOGS: Catalogs<SlackCopyCatalog> = { en: EN, es: ES };
